import {
    blockedItemReward,
    ILockedItem,
    ItemIDs,
    itemUnlockInfo,
    levelRewardItems,
    rewardCoinsBase,
} from '../../Config/Items';
import { getLoadedBoard } from '../../Config/Levels';
import Core from '../../Core/Core';
import {
    availableCollections,
    clampProgress,
    getTargetProgress,
    getUnlockedCollections,
} from '../../Utils/CollectableUtils';
import { Pausable } from '../../Utils/Pausable';
import { Timer } from '../../Utils/Timer';
import { getRandom } from '../../Utils/Utils';
import * as fromActions from '../Actions/Actions';
import { Actions, ActionTypes } from '../Actions/Actions';
import { /* TGameAreas, */ TransactionActionTypes } from '../../DeltaTracking/ResourceParamBuilder';
import { StuckReasons } from './level';
import { defaultScore } from './scoreHelpers';
// import ResourceTracking from "../../Managers/Tracking/ResourceTracking";

export const COMBO_BASE_TIME: number = 8000;
let comboTimer: Timer;
let winTimer: Timer;

export type IRewards = Array<IRewardItem | IRewardItem[]>;

export interface IRewardItem {
    id: string;
    val: number | IRewardItem[];
    chance?: number;
}

export interface IStateScore {
    score: number;
    multiplier: number;
    tiers: number[];
    totalScore: number;
    comboReset: boolean;
    winTime: number;
}

export const StateScore = (state = defaultScore, action: fromActions.Actions): IStateScore => {
    switch (action.type) {
        case ActionTypes.START_LEVEL:
            clearMultiplier();
            const loadedData = getLoadedBoard();
            const dataTiers = loadedData.config.levelScoreTiers;
            const levelTiers = dataTiers || defaultScore.tiers;
            return {
                ...defaultScore,
                tiers: levelTiers,
                multiplier: 1,
                score: loadedData.config.winTime,
            };
        case ActionTypes.SET_LEVEL_SCORE:
            return {
                ...state,
                score: action.payload,
            };
        case ActionTypes.ADD_LEVEL_SCORE:
            return {
                ...state,
                score: state.score + action.payload,
            };
        case ActionTypes.RESET_COMBO:
            return {
                ...state,
                comboReset: action.payload,
            };
        case ActionTypes.SET_MULTIPLIER:
            return {
                ...state,
                multiplier: setM(state, action.payload),
            };
        case ActionTypes.SET_WIN_TIME:
            return {
                ...state,
                ...setWinTimer(action.payload),
            };
        case ActionTypes.ADD_WIN_TIME:
            return {
                ...state,
                ...addTime(action.payload),
            };
        case ActionTypes.LEVEL_STUCK:
            if (action.payload.stuck && action.payload.reason === StuckReasons.TIME) {
                return {
                    ...state,
                    ...setWinTimer(0),
                };
            }
            return state;
        case ActionTypes.COMPLETE_LEVEL:
            if (winTimer) {
                const winTime = winTimer.remainingTime / 1000;
                destroyWinTimer();
                return {
                    ...state,
                    winTime,
                };
            }
            return state;
        default:
            return state;
    }
};

const destroyWinTimer = () => {
    if (winTimer) {
        winTimer.destroy();
        winTimer = null;
    }
};

const addTime = (time: number) => {
    const remainingTime = winTimer ? winTimer.remainingTime : 0;
    return setWinTimer(remainingTime / 1000 + time);
};

const setWinTimer = (time: number) => {
    destroyWinTimer();
    if (time > 0) {
        winTimer = new Timer(
            1000 * time,
            () => {
                Core.store.dispatch(Actions.setLevelStuck({ reason: StuckReasons.TIME }));
            },
            Pausable.isPaused
        );
    }
    return { winTime: time };
};

const clearMultiplier = () => {
    if (comboTimer) {
        comboTimer.destroy();
    }
};

export const setM = (state: IStateScore, delta: number) => {
    const multiplier = state.multiplier || 1;
    const newMultiplier = Math.max(Math.min(multiplier + delta, 10), 1);

    if (comboTimer) {
        comboTimer.destroy();
    }

    if (newMultiplier > 1) {
        const comboCallback = () => {
            Core.store.dispatch(Actions.setMultiplier(-state.multiplier));
        };
        comboTimer = new Timer(
            COMBO_BASE_TIME - 500 * (newMultiplier - 1),
            comboCallback,
            Pausable.isPaused
        );
    }
    return newMultiplier;
};

export const replaceBlockedItem = (loot: IRewardItem) => {
    const levelIndex = Core.store.getState().StatePlayer.lastLevelIndex;
    const allCollectablesUnlocked =
        getUnlockedCollections().length === availableCollections().length;
    const blockedItem: ILockedItem = itemUnlockInfo.find(
        x =>
            (loot.id === ItemIDs.COLLECTABLE_TOKEN && allCollectablesUnlocked) ||
            (x.id === loot.id && levelIndex < x.unlockLevel)
    );
    if (blockedItem) {
        return blockedItemReward;
    }
    if (loot.id === ItemIDs.COLLECTABLE_TOKEN) {
        loot.val = clampProgress(loot.val as number);
    }

    return loot;
};

// from map chest rewards, and win sequence
// .....aaaaaand from mission milestones..
// .....and from SocialHubPopup (reward after 10 send coins)
export const storeRewards = (rewards: IRewardItem[], context: TransactionActionTypes) => {
    rewards.map(r => {
        if (r.id === 'Box') {
            return;
        }
        const rewardVal = r.val as number;
        const boosterId = r.id as ItemIDs;

        /* const trackId = boosterId.toLowerCase();
        const tArea =
            context === TransactionActionTypes.WIN_SEQUENCE ? TGameAreas.LEVEL : TGameAreas.MAP;
        ResourceTracking.sendRewardResourceEvent(trackId, rewardVal, context, tArea);
        */

        if (r.id === ItemIDs.COINS) {
            Core.store.dispatch(Actions.updateCoins(rewardVal));
            return;
        }

        if (r.id === ItemIDs.HEART) {
            Core.store.dispatch(Actions.updateHearts(rewardVal));
            return;
        }

        if (r.id === ItemIDs.COLLECTABLE_TOKEN) {
            const targetProgress = getTargetProgress(r.val as number);
            Core.store.dispatch(Actions.setCollectablesProgress(targetProgress));
            return;
        }
        for (let i = 0; i < rewardVal; i++) {
            Core.store.dispatch(Actions.storeBooster([boosterId]));
        }
    });
};

export const getLevelReward = (starScore: number, replay: boolean = false): IRewardItem[] => {
    const rewards: IRewardItem[] = [];
    let i = 0;
    while (i < starScore) {
        if (replay) {
            rewards.push({ id: 'Coins', val: i + 1 });
        } else {
            const r = { id: 'Coins', val: rewardCoinsBase[i] };
            if (i === 2) {
                const loot = chooseLoot();
                const box = { id: 'Box', val: [r, loot] };
                rewards.push(box);
            } else {
                rewards.push(r);
            }
        }

        i++;
    }

    return rewards;
};

const chooseLoot = (): IRewardItem => {
    const levelIndex = Core.store.getState().StatePlayer.lastLevelIndex;

    const rewardItem = itemUnlockInfo.find(x => levelIndex === x.unlockLevel && x.reward);
    if (rewardItem) {
        return levelRewardItems.find(x => x.id === rewardItem.id);
    }
    const ramdomReward = getRandomReward(levelRewardItems);
    return replaceBlockedItem(ramdomReward);
};

export const getRandomReward = (rewards: IRewardItem[]): IRewardItem => {
    const totalChance = rewards.reduce((prev, cur) => {
        return prev + cur.chance;
    }, 0);
    const random = getRandom(0, totalChance);
    let sum = 0;
    return rewards.find(l => {
        sum += l.chance;
        return random <= sum;
    });
};
